import { Button, Col, ConfigProvider, DatePicker, Form, Row, Space, Table } from 'antd';
import { useRef, useState } from 'react';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { rangePresets } from '../../../../common/utils/common.utils';
import { excelDateRangeFormat } from '../../../../common/utils/excelDateRangeFormat';
import DownloadExcelReport from '../../../../components/common/ExcelReport';
import { FormButton } from '../../../../components/common/FormItem/FormItems';
import { SelectClients } from '../../../../components/common/FormItem/SelectCustomFeilds';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import FormHeaderTitle from '../../../Invoice(Visa)/Components/FormHeaderTitle';
import { useLazyGetJourneyDateWiseClientsQuery } from '../endpoints/JourneyDateWiseClientsEndpoints';
import { IJourneyDateWiseFormDataType } from '../types/JourneyDateWiseClientsTypes';
import JourneyDateWiseClientsColumn from '../utils/JourneyDateWiseClientsColumn';

const JourneyDateWiseClients = () => {
  const [paramData, setParamData] = useState<{
    date_range: string;
    id: number | string;
  }>();
  const [form] = Form.useForm();
  const currentDate = dayjs().format('DD-MM-YYYY');
  const [dateRange, setDateRange] = useState(`from_date=${currentDate}&to_date=${currentDate}`);

  const [fetchJourneyWiseClients, { data, isLoading, isFetching }] =
    useLazyGetJourneyDateWiseClientsQuery();

  const onFinish = async (values: IJourneyDateWiseFormDataType) => {
    const setFrom_date = dayjs(values.date_range[0]).format('YYYY-MM-DD');
    const setTo_date = dayjs(values.date_range[1]).format('YYYY-MM-DD');
    const date_range = `from_date=${setFrom_date}&to_date=${setTo_date}`;
    setDateRange(date_range);
    const body = {
      date_range: date_range,
      comb_client: values.invoice_combclient_id,
    };
    await fetchJourneyWiseClients({
      comb_client: body.comb_client,
      query: `?${body.date_range}`,
    });
    setParamData({
      date_range: body.date_range,
      id: '',
    });
  };

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'Journey Date Wise Clients Report',
  });

  // @HANDLE CONTENTS
  const jsx_com = (
    <ConfigProvider>
      <Table
        size='small'
        bordered
        columns={JourneyDateWiseClientsColumn(paramData)}
        loading={{
          spinning: isLoading || isFetching,
          indicator: loadingIndicator,
        }}
        dataSource={data?.data}
        pagination={false}
      />
    </ConfigProvider>
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'Journey Date Wise Clients Report' }}
        extraInfo={{
          date:
            paramData?.date_range
              ?.split('&')
              ?.map((param) => dayjs(param.split('=')[1])?.format('DD-MM-YYYY'))
              ?.join(' to ') || '',
        }}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'Journey Date Wise Clients Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>

        <DownloadExcelReport
          excelName={`Journey_Date_Wise_Client_${excelDateRangeFormat(dateRange as string)}`}
          description={`JOURNEY DATE WISE CLIENT REPORT`}
          excelTableHead={[
            'Invoice Date',
            'Invoice No',
            'Ticket No.',
            'Client Name',
            'Pax Name',
            'PNR',
            'Airline',
            'Route',
            'Journey Date',
            'Return Date',
          ]}
          excelData={
            data?.data?.length
              ? data.data.map((item: any) => ({
                  'Invoice Date': item.create_date
                    ? dayjs(item.create_date).format('DD-MM-YYYY')
                    : '',
                  'Invoice No': item.invoice_no || 'N/A',
                  'Ticket No.': item.airticket_ticket_no || 'N/A',
                  'Client Name': item.client_name || 'N/A',
                  'Pax Name': item.pass_name || 'N/A',
                  PNR: item.airticket_pnr || 'N/A',
                  Airline: item.airline_name || 'N/A',
                  Route: item.airticket_routes || 'N/A',
                  'Journey Date': item.airticket_journey_date
                    ? dayjs(item.airticket_journey_date).format('DD-MM-YYYY')
                    : '',
                  'Return Date': item.airticket_return_date
                    ? dayjs(item.airticket_return_date).format('DD-MM-YYYY')
                    : '',
                }))
              : []
          }
        />
      </Space>
      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{
          invoice_combclient_id: 'all',
          date_range: [dayjs(), dayjs()],
        }}
      >
        <Row justify={'space-between'}>
          <Col>
            <Row gutter={16}>
              <SelectClients
                name='invoice_combclient_id'
                label='Select Client :'
                placeholder='Select client'
                offDropDown
                showAll
              />
              <Col span={6} xs={24} sm={24} md={24} lg={12}>
                <Form.Item
                  label='Date Range'
                  name='date_range'
                  rules={[{ required: true, message: ` Date Range is required!` }]}
                >
                  <DatePicker.RangePicker
                    presets={rangePresets}
                    format={'DD-MM-YYYY'}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col lg={2}>
                <FormButton label='Search' icon textAlign='left' />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <FormHeaderTitle title='Journey Date Wise Clients Report' />

      {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
      {jsx_com}

      {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
      {print_content}
    </>
  );
};

export default JourneyDateWiseClients;
